/**
 * Path validation for downloaded export photos (AKL-554).
 *
 * Kept in its own module, free of imports, so it is unit-testable: the downloader
 * itself pulls in `client-zip` and the axios client, and Jest cannot load either
 * in this project (the documented ESM limitation).
 */

/**
 * Validate a server-supplied relative path before it becomes a zip entry name.
 *
 * The API builds `relative_path` from the same function that writes
 * `media.filePath`, which already basenames the file — but an entry name goes
 * straight into an archive the user extracts, and a `..` or absolute path there
 * is a zip-slip: some extractors will happily write outside the target folder.
 * The desktop tool validates the identical field in Rust before writing to disk;
 * not doing the same here would leave the browser the weaker of the two paths.
 *
 * Rejects absolute paths, Windows drive prefixes, UNC roots and any `..`
 * segment. Both separators are accepted because the API emits POSIX ones and
 * nothing stops a caller normalising first.
 * @param relativePath the path as received from the API
 * @returns a normalised POSIX path, or null when it must not be trusted
 */
export function safeEntryName(relativePath: string): string | null {
  const normalised = relativePath.replace(/\\/g, "/").trim();
  if (normalised === "") return null;
  if (normalised.startsWith("/")) return null;
  if (/^[a-zA-Z]:/.test(normalised)) return null;

  const segments: string[] = [];
  for (const segment of normalised.split("/")) {
    if (segment === "" || segment === ".") continue;
    if (segment === "..") return null;
    segments.push(segment);
  }
  return segments.length === 0 ? null : segments.join("/");
}
